import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import clsx from "clsx";
import { Button, Input } from "@/ui";
import styles from "./useSearch.module.css";

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

function ClearIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function SearchFieldComponent({ placeholder, helperText, submitButton, suggestions = [], searchInput, onInputChange, onSearch, onReset, onSuggestionSelect }) {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const containerRef = useRef(null);

  // Stable ids so the combobox can point at its listbox and active option.
  // Generated rather than hardcoded — two search fields can coexist on a page.
  const reactId = useId();
  const inputId = `${reactId}-search`;
  const listboxId = `${reactId}-suggestions`;
  const helperId = `${reactId}-helper`;
  const optionId = (idx) => `${listboxId}-option-${idx}`;

  const filtered = searchInput.trim()
    ? suggestions.filter((s) => s.toLowerCase().includes(searchInput.toLowerCase())).slice(0, 8)
    : [];

  const showDropdown = open && filtered.length > 0;

  const select = useCallback((value) => {
    onInputChange({ target: { value } });
    const handled = onSuggestionSelect?.(value);
    if (!handled) onSearch(value);
    setOpen(false);
    setActiveIdx(-1);
  }, [onInputChange, onSearch, onSuggestionSelect]);

  const handleKeyDown = (e) => {
    if (!showDropdown) {
      if (submitButton && e.key === "Enter") onSearch();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIdx >= 0) {
        select(filtered[activeIdx]);
      } else {
        const handled = onSuggestionSelect?.(searchInput);
        if (!handled) onSearch();
      }
      setOpen(false);
    } else if (e.key === "Escape") {
      setOpen(false);
      setActiveIdx(-1);
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className={styles.row}>
      <div ref={containerRef} className={styles.field}>
        <div className={styles.inputWrap}>
          <Input
            id={inputId}
            placeholder={placeholder}
            value={searchInput}
            startIcon={<SearchIcon />}
            endIcon={searchInput ? (
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onReset(); setOpen(false); }}
                className={styles.clearButton}
                aria-label="Clear search"
              >
                <ClearIcon />
              </button>
            ) : undefined}
            onChange={(e) => { onInputChange(e); setOpen(true); setActiveIdx(-1); }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            role="combobox"
            aria-expanded={showDropdown}
            aria-controls={listboxId}
            aria-haspopup="listbox"
            aria-autocomplete="list"
            aria-activedescendant={
              showDropdown && activeIdx >= 0 ? optionId(activeIdx) : undefined
            }
            aria-describedby={helperText ? helperId : undefined}
          />
          {showDropdown && (
            <ul id={listboxId} role="listbox" aria-label="Search suggestions" className={styles.dropdown}>
              {filtered.map((item, idx) => (
                <li
                  key={item}
                  id={optionId(idx)}
                  role="option"
                  aria-selected={idx === activeIdx}
                  onMouseDown={(e) => { e.preventDefault(); select(item); }}
                  className={clsx(styles.option, idx === activeIdx && styles.optionActive)}
                  onMouseEnter={() => setActiveIdx(idx)}
                  onMouseLeave={() => setActiveIdx(-1)}
                >
                  {item}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className={styles.srOnly} role="status" aria-live="polite">
          {showDropdown ? `${filtered.length} suggestions available` : ""}
        </div>
        {helperText && (
          <div id={helperId} className={styles.helper}>
            {helperText}
          </div>
        )}
      </div>
      {submitButton && (
        <Button type="button" variant="secondary" onClick={() => {
          const handled = onSuggestionSelect?.(searchInput);
          if (!handled) onSearch();
        }}>
          Search
        </Button>
      )}
    </div>
  );
}

export default function useSearch(setPage, { placeholder = "Search...", helperText, submitButton = false, suggestions = [], onSuggestionSelect, initialValue = "" } = {}) {
  const [search, setSearch] = useState(initialValue);
  const [searchInput, setSearchInput] = useState(initialValue);

  const handleSearch = useCallback((explicitValue) => {
    const value = explicitValue !== undefined ? explicitValue : searchInput;
    setSearch(value);
    setSearchInput(value);
    setPage(1);
  }, [searchInput, setPage]);

  const reset = () => {
    setSearch("");
    setSearchInput("");
    setPage(1);
  };

  const debouncedSearch = useDebouncedCallback(handleSearch, 500);

  const handleInputChange = useCallback((event) => {
    const value = event.target.value;
    setSearchInput(value);
    if (!submitButton) {
      debouncedSearch(value);
    } else if (value === "") {
      setSearch("");
      setPage(1);
    }
  }, [submitButton, debouncedSearch, setPage]);

  const SearchField = (
    <SearchFieldComponent
      placeholder={placeholder}
      helperText={helperText}
      submitButton={submitButton}
      suggestions={suggestions}
      searchInput={searchInput}
      onInputChange={handleInputChange}
      onSearch={handleSearch}
      onReset={reset}
      onSuggestionSelect={onSuggestionSelect}
    />
  );

  return { SearchField, search, searchInput, reset };
}
